/**
 * Review highlights (THEME.md §7.16; reviews spec H3; T-148, T-153): the adapter for Judge.me's Testimonials
 * Carousel before `blocks/okt-review-controls.liquid` (Review cards). Judge.me builds its cards after load, as a
 * fade of one card at a time; `okt-judgeme-testimonials.css` lays them out as our cards and `okt-slider-progress`
 * moves the row. Here, on each of Judge.me's builds, followed with a MutationObserver (no polling):
 * - only reviews written in the page's language: Judge.me returns every featured review in every language (the
 *   page's first) and keeps them in `jdgmCarouselMode`; a card whose review is in another language is hidden.
 *   Reviews Judge.me gives no language show everywhere, as in Judge.me's own language filter;
 * - every card reachable: Judge.me's fade sets `inert` and `aria-hidden` on all cards but one; undone;
 * - the row a list, its cards list items, and the region named by the section's heading;
 * - a card is a quote, not a button: a click doesn't reach Judge.me (its lightbox);
 * - Square and Portrait cards: the text clamped to the lines that fit (`--okt-review-fit`), again on resize;
 * - no empty band: the section hides when cards came but none is in the page's language, or when no card has
 *   come 3 s after load (nothing chosen, Judge.me unreachable), except in the theme editor, and it shows again if
 *   cards come later;
 * - the theme editor re-renders the section on every change: it attaches again on `shopify:section:load`.
 */

/** The page's language, as Judge.me reads it (`primary_language`), without a region */
const language = (document.documentElement.lang || '').split('-')[0].toLowerCase();

/**
 * Hides the cards of reviews in another language
 * @param {HTMLElement} widget
 * @param {Element} row
 */
const filter = (widget, row) => {
  const reviews = /** @type {any} */ (window).jdgmCarouselMode?.[widget.dataset.jdgmBlockId ?? '']?.reviews;
  if (!language || !Array.isArray(reviews)) return;

  const languages = new Map(reviews.map((review) => [review.uuid, review.language]));
  for (const card of row.children) {
    if (!(card instanceof HTMLElement) || !card.dataset.reviewId) continue;
    const other = String(languages.get(card.dataset.reviewId) || language).split('-')[0].toLowerCase() !== language;
    if (card.hidden !== other) card.hidden = other;
  }
};

/**
 * Square and Portrait cards: the text's line clamp to the lines that fit; Auto keeps Text lines. Every card is
 * measured first and written after (T-269): a write between two cards' reads made the next read restyle and lay
 * out the page again, once per card. A card's text fills what its own size leaves, so the values are the same.
 * @param {Element} row
 */
const fit = (row) => {
  /** @type {Array<[HTMLElement, string | null]>} */
  const cards = [];
  for (const card of row.children) {
    if (!(card instanceof HTMLElement) || card.hidden) continue;
    const text = card.querySelector('.jdgm-text');
    const paragraph = text?.querySelector('p');
    if (!(text instanceof HTMLElement) || !paragraph) continue;

    if (getComputedStyle(card).aspectRatio === 'auto') {
      cards.push([card, null]);
      continue;
    }

    const lines = String(
      Math.max(1, Math.floor((text.clientHeight + 1) / parseFloat(getComputedStyle(paragraph).lineHeight)))
    );
    cards.push([card, lines]);
  }

  for (const [card, lines] of cards) {
    if (lines === null) card.style.removeProperty('--okt-review-fit');
    else if (card.style.getPropertyValue('--okt-review-fit') !== lines) card.style.setProperty('--okt-review-fit', lines);
  }
};

/** @param {HTMLElement} widget */
const prepare = (widget) => {
  const row = widget.querySelector('.jdgm-testimonials-container');
  if (!row) return;
  if (row.getAttribute('role') !== 'list') row.setAttribute('role', 'list');
  for (const card of row.children) {
    if (card instanceof HTMLElement && card.inert) card.inert = false;
    if (card.hasAttribute('aria-hidden')) card.removeAttribute('aria-hidden');
    if (card.getAttribute('role') !== 'listitem') card.setAttribute('role', 'listitem');
  }
  filter(widget, row);
  fit(row);
};

/** @param {Element} controls */
const attach = (controls) => {
  const widget = controls.previousElementSibling?.querySelector('.jdgm-testimonials-carousel');
  if (!(widget instanceof HTMLElement) || widget.dataset.okt != null) return;
  widget.dataset.okt = '';

  const section = widget.closest('.shopify-section');
  const heading = section?.querySelector('h1, h2, h3, h4')?.textContent?.trim();
  if (heading) widget.setAttribute('aria-label', heading);

  widget.addEventListener(
    'click',
    (event) => {
      const target = event.target instanceof Element ? event.target : null;
      if (target?.closest('.jdgm-card') && !target.closest('a')) event.stopPropagation();
    },
    true
  );

  // The section hides without a card to show: at once when the cards came in other languages only, 3 s after
  // load when none came; it shows again when cards come later
  let checked = false;
  const update = () => {
    if (!(section instanceof HTMLElement) || window.Shopify?.designMode) return;
    const empty = !widget.querySelector('.jdgm-card:not([hidden])') && (checked || !!widget.querySelector('.jdgm-card'));
    if (empty === (section.dataset.oktEmpty != null)) return;
    section.hidden = empty;
    if (empty) section.dataset.oktEmpty = '';
    else delete section.dataset.oktEmpty;
  };

  new MutationObserver(() => {
    prepare(widget);
    update();
  }).observe(widget, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['inert', 'aria-hidden'],
  });
  prepare(widget);

  // A new width changes the cards' size, so the lines that fit
  new ResizeObserver(() => {
    const row = widget.querySelector('.jdgm-testimonials-container');
    if (row) fit(row);
  }).observe(widget);

  const check = () =>
    setTimeout(() => {
      checked = true;
      update();
    }, 3000);
  if (document.readyState === 'complete') check();
  else window.addEventListener('load', check, { once: true });
};

const run = () => document.querySelectorAll('.okt-review-controls').forEach(attach);
run();
document.addEventListener('shopify:section:load', run);
